import React, {useCallback, useMemo} from 'react';
import {Box, Typography} from '@mui/material';
import {StandaloneCheckbox} from '../../../components/base/checkbox/checkbox';
import PartnerDataGrid from '../../../containers/data-grid';
import {DataGridColumnAlignments, DataGridColumnTypes, useDataGridSavedState,} from '../../../../packages/data-grid';
import {formatMoney, htmlToString} from '../../../../core/services/utils/utils';
import useAccessLevels from '../../../hooks/use-access-levels';
import {ProductTitleText} from '../../../views/panel/operator/products/single';
import QuantityStepper from './quantity-stepper';
import {SuggestiveSellingDataGridColumnNames, suggestiveSellingDataGridSaveConfig,} from '../constants';
import {getPartQuantity, isPartSelected} from '../utils/suggestive-selling-staging';

const SuggestiveSellingDataGrid = ({
                                       parts,
                                       staging,
                                       onQuantityChange,
                                       onSelectionChange,
                                       disabled,
                                   }) => {
    const operatorAccessLevels = useAccessLevels();

    const handleCheckboxChange = useCallback((partId, checked) => {
        onSelectionChange(partId, checked);
    }, [onSelectionChange]);

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]} */
        [
            {
                name: SuggestiveSellingDataGridColumnNames.select,
                title: '',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                resizable: false,
                width: 0,
            },
            {
                name: SuggestiveSellingDataGridColumnNames.part,
                title: 'Part',
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinned: true,
                width: 150,
            },
            ...(operatorAccessLevels.packages.sales.pricesInLookup
                ? [{
                    name: SuggestiveSellingDataGridColumnNames.price,
                    title: 'Price',
                    alignment: DataGridColumnAlignments.right,
                    type: DataGridColumnTypes.element,
                    sortable: false,
                    width: 100,
                }]
                : []),
            {
                name: SuggestiveSellingDataGridColumnNames.quantity,
                title: 'Quantity',
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 160,
            },
            {
                name: SuggestiveSellingDataGridColumnNames.description,
                title: 'Description',
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                width: 400,
            },
        ], [operatorAccessLevels.packages.sales.pricesInLookup]);

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns,
        version: suggestiveSellingDataGridSaveConfig.version,
        name: suggestiveSellingDataGridSaveConfig.name,
    });

    const useColumnVirtualization = useMemo(() => dataGridColumns.length > 10, [dataGridColumns.length]);

    const dataGridRows = useMemo(() => parts.map((part) => {
        const quantity = getPartQuantity(staging, part.partId);
        const selected = isPartSelected(staging, part.partId);
        const priceLabel = operatorAccessLevels.packages.sales.pricesInLookup
        && part.priceInfo?.newPrice != null
            ? formatMoney(part.priceInfo.newPrice)
            : null;

        return {
            key: part.partId,
            data: part,
            cells: {
                [SuggestiveSellingDataGridColumnNames.select]: (
                    <Box sx={{display: 'flex', justifyContent: 'center'}}>
                        <StandaloneCheckbox
                            checked={selected}
                            disabled={disabled}
                            onChange={(_, checked) => handleCheckboxChange(part.partId, checked)}
                        />
                    </Box>
                ),
                [SuggestiveSellingDataGridColumnNames.part]: (
                    <ProductTitleText
                        partNo={part.partNumber}
                        title={part.title ?? ''}
                    />
                ),
                [SuggestiveSellingDataGridColumnNames.description]: (
                    <Typography variant={'body2'} color={'text.secondary'}>
                        {htmlToString(part.description ?? '', 200) || '—'}
                    </Typography>
                ),
                ...(operatorAccessLevels.packages.sales.pricesInLookup && {
                    [SuggestiveSellingDataGridColumnNames.price]: (
                        <Typography variant={'body2'}>
                            {priceLabel ?? '—'}
                        </Typography>
                    ),
                }),
                [SuggestiveSellingDataGridColumnNames.quantity]: (
                    <Box sx={{display: 'flex', justifyContent: 'center'}}>
                        <QuantityStepper
                            value={quantity}
                            onChange={(next) => onQuantityChange(part.partId, next)}
                            disabled={disabled}
                            min={operatorAccessLevels.packages.sales.issueCredit ? undefined : 0}
                        />
                    </Box>
                ),
            },
        };
    }), [
        disabled,
        handleCheckboxChange,
        onQuantityChange,
        operatorAccessLevels.packages.sales.issueCredit,
        operatorAccessLevels.packages.sales.pricesInLookup,
        parts,
        staging,
    ]);

    return (
        <PartnerDataGrid
            {...suggestiveSellingDataGridSaveConfig}
            columns={dataGridColumns}
            initialLayoutState={{columns}}
            rows={dataGridRows}
            classNames={{
                container: 'suggestive-selling-data-grid-container',
                layout: 'virtualized-grid-list',
                toolbar: 'pt-0',
            }}
            hideFooter
            hideExporting
            hideToolbar
            useVirtualizedRows
            useVirtualizedColumns={useColumnVirtualization}
            visibleRows={8}
            state={{
                pagination: {
                    pageSize: parts.length,
                    currentPage: 1,
                    length: parts.length,
                },
                density: dataGridDensity,
            }}
        />
    );
};

export default SuggestiveSellingDataGrid;
